import React from 'react';
import { BRAND_COLORS } from '../../brand/tokens';
import { useTranslation } from '../../i18n';
import { useDiscreet } from '../../security/discreet';
import { WORDMARK_HEART_PATH } from './wordmarkData';

/**
 * The two logo characters as inline SVG, drawn with one thick line in
 * `currentColor` (ink on cream, cream on ink: the caller sets the text colour)
 * and the lipstick accents in `BRAND_COLORS.lipstick`. Paths come from
 * `womanFlork` / `manFlork` in `src/brand/logo/build.mjs`. They are
 * illustrations for chrome, marketing and empty states; nothing renders in
 * discreet mode.
 */

/** Tight boxes (stroke included) of each character in the logo's local frame. */
const HER_BOX = { x: 52, y: 26, width: 316, height: 536 } as const;
const HIM_BOX = { x: 17, y: 32, width: 406, height: 520 } as const;
const PAIR_GAP = 110;
const PAIR_BOX = {
  x: HER_BOX.x,
  y: 24,
  width: HER_BOX.width + PAIR_GAP + HIM_BOX.width,
  height: 540,
} as const;
const HIM_OFFSET_X = HER_BOX.x + HER_BOX.width + PAIR_GAP - HIM_BOX.x;

const LINE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 16,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const Dot: React.FC<{ cx: number; cy: number; r: number; lip?: boolean }> = ({
  cx,
  cy,
  r,
  lip = false,
}) => (
  <circle
    cx={cx}
    cy={cy}
    r={r}
    fill={lip ? BRAND_COLORS.lipstick : 'currentColor'}
    stroke="none"
  />
);

const HerFigure: React.FC = () => (
  <g {...LINE}>
    <path d="M196 46C280 16 346 72 344 156C342 212 322 250 312 290C306 316 308 350 314 384C318 404 318 420 318 436" />
    <path d="M196 46C120 74 92 140 104 196C112 236 136 262 150 292C120 310 74 350 66 392C58 440 100 470 160 466C210 462 250 440 262 410C270 390 262 372 250 362" />
    <path d="M176 464V550" />
    <path d="M250 430C262 470 270 510 276 550" />
    <path d="M330 232C372 262 362 316 312 324" />
    <path d="M160 62C120 30 72 50 66 110C62 160 86 200 92 240" />
    <Dot cx={262} cy={158} r={9} />
    <Dot cx={306} cy={148} r={9} />
    <path
      d="M250 146L244 136M262 144L262 132M274 148L280 138M294 136L288 126M306 134L306 122M318 138L324 128"
      strokeWidth={6}
    />
    <path
      d="M262 198C280 214 304 210 320 192"
      stroke={BRAND_COLORS.lipstick}
      strokeWidth={14}
    />
    <Dot cx={240} cy={184} r={10} lip />
    <Dot cx={330} cy={174} r={10} lip />
    <Dot cx={334} cy={206} r={4} />
  </g>
);

const HimFigure: React.FC = () => (
  <g {...LINE}>
    <path d="M250 50C180 30 110 60 100 150C94 210 110 280 140 340C150 360 154 390 154 420" />
    <path d="M250 50C310 70 346 130 336 200C330 250 320 300 300 340C292 360 292 390 292 420" />
    <path d="M154 420V540" />
    <path d="M292 420C290 460 286 500 280 540" />
    <path d="M118 250C80 240 40 200 30 160C26 140 36 120 56 128" />
    <path
      d="M42 196C46 176 72 170 92 180C100 200 84 216 66 214Z"
      fill="currentColor"
      stroke="none"
    />
    <path d="M322 250C360 240 400 200 410 160C414 140 404 120 384 128" />
    <path
      d="M398 196C394 176 368 170 348 180C340 200 356 216 374 214Z"
      fill="currentColor"
      stroke="none"
    />
    <Dot cx={96} cy={250} r={16} />
    <Dot cx={344} cy={250} r={16} />
    <Dot cx={158} cy={150} r={9} />
    <Dot cx={206} cy={142} r={9} />
    <path d="M140 196C170 222 212 218 236 190" strokeWidth={12} />
    <path d="M144 122L172 116M196 110L224 104" strokeWidth={8} />
  </g>
);

export interface MascotProps {
  /** Rendered height in px (the width follows the character's proportions). */
  size?: number;
  className?: string;
  /** Default true: the doodles only decorate. false adds an accessible name. */
  decorative?: boolean;
}

interface FrameProps extends MascotProps {
  box: { x: number; y: number; width: number; height: number };
  label: string;
  testId: string;
  children: React.ReactNode;
}

const MascotFrame: React.FC<FrameProps> = ({
  box,
  label,
  testId,
  size = 160,
  className,
  decorative = true,
  children,
}) => {
  const discreet = useDiscreet();
  if (discreet) return null;
  const width = Math.round((size * box.width) / box.height);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
      width={width}
      height={size}
      className={className}
      focusable="false"
      data-testid={testId}
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
    >
      {children}
    </svg>
  );
};

/** Her: hand on hip, ponytail, lashes, lipstick smirk. */
export const MascotHer: React.FC<MascotProps> = props => {
  const { t } = useTranslation();
  return (
    <MascotFrame
      {...props}
      box={HER_BOX}
      label={t('brand.mascot.her')}
      testId="mascot-her"
    >
      <HerFigure />
    </MascotFrame>
  );
};

/** Him: flexing, cheeky grin. */
export const MascotHim: React.FC<MascotProps> = props => {
  const { t } = useTranslation();
  return (
    <MascotFrame
      {...props}
      box={HIM_BOX}
      label={t('brand.mascot.him')}
      testId="mascot-him"
    >
      <HimFigure />
    </MascotFrame>
  );
};

/** Both facing each other with a heart between them. */
export const MascotPair: React.FC<MascotProps> = props => {
  const { t } = useTranslation();
  const heartX = HER_BOX.x + HER_BOX.width + PAIR_GAP / 2;
  const heartScale = 1.5;
  return (
    <MascotFrame
      {...props}
      box={PAIR_BOX}
      label={t('brand.mascot.pair')}
      testId="mascot-pair"
    >
      <HerFigure />
      <g transform={`translate(${HIM_OFFSET_X} 0)`}>
        <HimFigure />
      </g>
      <path
        d={WORDMARK_HEART_PATH}
        fill={BRAND_COLORS.lipstick}
        transform={`translate(${heartX - 512 * heartScale} ${130 - 806 * heartScale}) scale(${heartScale})`}
      />
    </MascotFrame>
  );
};
